import type { ReactNode } from 'react'
import { FormattedNumber, useIntl } from 'react-intl'

import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import { SearchInput, SelectField } from './form'

type FilterOption = {
  value: string
  label: string
  /** Optional badge count rendered next to the label (e.g. per-status totals). */
  count?: number
}

type SelectOption = {
  value: string
  label: string
}

type TableFilterBarProps = {
  options: FilterOption[]
  value: string
  onValueChange: (value: string) => void
  search?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  selectLabel?: string
  selectAriaLabel?: string
  selectAllLabel?: string
  selectOptions?: SelectOption[]
  selectValue?: string
  onSelectChange?: (value: string) => void
  additionalFilters?: ReactNode
}

export const TableFilterBar = ({
  options,
  value,
  onValueChange,
  search,
  onSearchChange,
  searchPlaceholder,
  selectLabel,
  selectAriaLabel,
  selectAllLabel,
  selectOptions,
  selectValue,
  onSelectChange,
  additionalFilters,
}: TableFilterBarProps) => {
  const { formatMessage } = useIntl()

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <ToggleButtonGroup
        size="medium"
        value={value}
        onValueChange={onValueChange}
      >
        {options.map((opt) => (
          <ToggleButton
            key={opt.value}
            value={opt.value}
          >
            {opt.label}
            {/* `button/medium` is the one variant matching what this count previously
                INHERITED from the ToggleButton (Inter 14px), so moving the colour onto
                the prop stays a pure colour change; `caption` would switch the number
                to Poppins 12px mid-label, here and on both Assets filter bars. */}
            {opt.count != null && (
              <Typography
                className="ml-2 font-medium tabular-nums"
                color="text.secondary"
                component="span"
                variant="button/medium"
              >
                <FormattedNumber value={opt.count} />
              </Typography>
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <div className="flex flex-wrap items-center gap-3">
        {additionalFilters}
        {selectOptions !== undefined && onSelectChange !== undefined && (
          <SelectField
            ariaLabel={selectAriaLabel}
            className="min-w-[150px]"
            label={selectLabel}
            value={selectValue ?? ''}
            options={[
              { value: '', label: selectAllLabel ?? formatMessage({ id: 'common.all' }) },
              ...selectOptions,
            ]}
            onValueChange={onSelectChange}
          />
        )}
        {onSearchChange !== undefined && (
          <SearchInput
            placeholder={searchPlaceholder ?? formatMessage({ id: 'common.search' })}
            value={search ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        )}
      </div>
    </div>
  )
}
